/**
 * ExploreDirectionsSection — the "Explore directions" UI in the Tag Profile tab.
 *
 * Shows a button to trigger the directions pipeline, a loading state while
 * fetching, an error message on failure, and the suggestions list on success.
 * Clicking a suggestion fires the same tag search as clicking a tag in the
 * existing deck tag profile (AC-137).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useDirections } from '../../hooks/useDirections.js';
import { useSearchStore } from '../../stores/search-store.js';
import { useDeckStore } from '../../stores/deck-store.js';

const PROGRESS_STEPS = [
  'Fetching EDHREC themes...',
  'Getting high synergy cards...',
  'Analyzing Scryfall tags...',
  'Filtering suggestions...',
];

// ─── Inline loading spinner (matches TagProfile.tsx pattern) ──────────────────

function LoadingSpinner() {
  return (
    <div className="w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full animate-spin inline-block" />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ExploreDirectionsSection() {
  const { suggestions, error, canExplore, explore } = useDirections();
  const toggleTagFilter = useSearchStore((s) => s.toggleTagFilter);
  const clearSearch = useSearchStore((s) => s.clearSearch);
  const activeTagSlugs = useSearchStore((s) => s.activeTagSlugs);
  const setSelectedDirectionTag = useDeckStore((s) => s.setSelectedDirectionTag);
  const selectedDirectionTag = useDeckStore((s) => s.selectedDirectionTag);

  // Local loading state — React guarantees this triggers a render
  const [loading, setLoading] = useState(false);

  const handleExplore = useCallback(async () => {
    setLoading(true);
    try {
      await explore();
    } finally {
      setLoading(false);
    }
  }, [explore]);

  // Auto-trigger explore when commander + tag profile are ready and no cached results
  const tagProfile = useDeckStore((s) => s.tagProfile);
  const importGeneration = useDeckStore((s) => s.importGeneration);
  const exploreRef = useRef(handleExplore);
  exploreRef.current = handleExplore;
  useEffect(() => {
    if (canExplore && tagProfile && !suggestions && !loading) {
      void exploreRef.current();
    }
  }, [canExplore, tagProfile, suggestions, loading, importGeneration]);

  // Cycling progress indicator

  const [progressStep, setProgressStep] = useState(0);
  useEffect(() => {
    if (!loading) {
      setProgressStep(0);
      return;
    }
    const interval = setInterval(() => {
      setProgressStep((prev) => Math.min(prev + 1, PROGRESS_STEPS.length - 1));
    }, 2000);
    return () => clearInterval(interval);
  }, [loading]);

  return (
    <div className="mb-3">
      {/* Waiting state — no commander yet */}
      {!canExplore && !loading && !(suggestions && suggestions.length > 0) && (
        <p className="text-xs text-gray-500">Add a commander to discover tag directions.</p>
      )}

      {/* Progress indicator — replaces button while loading */}
      {loading && (
        <div className="py-2">
          <div className="flex items-center gap-2 mb-1">
            <LoadingSpinner />
            <span className="text-xs text-gray-300 font-medium">Exploring directions...</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
              <div
                className="bg-orange-500 h-full rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${((progressStep + 1) / PROGRESS_STEPS.length) * 100}%` }}
              />
            </div>
            <span className="text-[10px] tabular-nums text-gray-500">{progressStep + 1}/{PROGRESS_STEPS.length}</span>
          </div>
          <p className="text-[11px] text-gray-500 italic mt-1">{PROGRESS_STEPS[progressStep]}</p>
        </div>
      )}

      {/* Error message */}
      {!loading && error && (
        <p className="mt-2 text-xs text-red-400">{error}</p>
      )}

      {/* No suggestions found */}
      {!loading && !error && suggestions && suggestions.length === 0 && (
        <p className="mt-2 text-xs text-gray-500">No new directions found — your deck already covers the popular tags.</p>
      )}

      {/* Suggestions list */}
      {!loading && !error && suggestions && suggestions.length > 0 && (
        <div>
          <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
            {suggestions.map((suggestion) => {
              const isActive = activeTagSlugs.includes(suggestion.slug);
              const isSelected = selectedDirectionTag?.slug === suggestion.slug;
              return (
                <button
                  key={suggestion.slug}
                  onClick={(e: React.MouseEvent) => {
                    const multi = e.metaKey || e.ctrlKey;
                    if (!multi && isActive && activeTagSlugs.length === 1) {
                      clearSearch();
                      setSelectedDirectionTag(null);
                    } else {
                      toggleTagFilter(suggestion.slug, multi);
                      setSelectedDirectionTag(suggestion);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-colors text-left ${
                    isActive
                      ? 'bg-orange-500/20 text-orange-300'
                      : 'hover:bg-gray-800 text-gray-300'
                  }`}
                >
                  <span>{suggestion.displayName}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
